import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BaseComponent } from 'frontend/admin/app/shared/components/base-component/base.component';
import { CommonConstant } from 'frontend/admin/app/shared/constants/common.constant';
import { ObjectUtils } from 'frontend/admin/app/shared/utils/objects.utils';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface UserOption {
  label: string;
  value: string;
  shortImg?: string;
  order?: number;
}

@Component({
  selector: 'app-search-user-autocomplete',
  templateUrl: './search-user-autocomplete.component.html',
  styleUrls: ['./search-user-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchUserAutocompleteComponent extends BaseComponent implements OnChanges {
  @Input() data: UserOption[] = [];
  @Input() initValue: string[] = [];

  @Input() nameKey: string;
  @Input() placeholderInput: string;

  @Output() selectItem = new EventEmitter();

  public inputSearchForm = new FormControl();
  public filteredOptions: UserOption[] = [];

  public choseItems: UserOption[] = [];

  constructor(
    private readonly cd: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    super();
  }

  public onVisibleChange($event: boolean): void {
    if ($event && this.inputSearchForm?.value) {
      this.inputSearchForm?.setValue('');
    }
    const boardElement = this.document.getElementById('boardId');
    if (boardElement) {
      if (!$event) {
        boardElement?.classList.remove('hidden__scroll');
      } else {
        boardElement?.classList.add('hidden__scroll');
      }
    }

    if ($event) {
      this.filteredOptions = this.data.filter((item) => !this.choseItems.find(({ value }) => item.value === value) && item.value);
      this.data = this.filteredOptions;
    }
  }

  public onRemove($event: Event, item: UserOption): void {
    this.choseItems = this.choseItems.filter(x => x.value !== item.value);
    this.filteredOptions.push(item);
    this.optimizeList();
    $event.preventDefault();
    $event.stopPropagation();
  }

  public onChange(value: string): void {
    this.filteredOptions = this.data.filter(option => option.label.toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  public onSelect(option: UserOption): void {
    this.choseItems.push(option);
    this.filteredOptions = this.filteredOptions.filter(x => x.value !== option.value);
    this.optimizeList();
  }

  protected override onInit(): void {
    this.subscribe(
      this.inputSearchForm.valueChanges.pipe(debounceTime(CommonConstant.INPUT_DEBOUNCE_INTERVAL), distinctUntilChanged()),
      searchValue => {
        this.filteredOptions = this.data.filter(option => option.label.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
        this.cd.detectChanges();
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      const { currentValue, previousValue } = changes?.['data'];
      if (currentValue && !ObjectUtils.isComparisonObject(currentValue, previousValue) && currentValue?.length > 0) {
        this.filteredOptions = currentValue.map((x, i) => {
          x.order = i;
          return x;
        });
      }
    }

    if (changes['initValue']) {
      const { currentValue, previousValue } = changes?.['initValue'];
      if (currentValue && !ObjectUtils.isComparisonObject(currentValue, previousValue) && currentValue?.length > 0) {
        this.data.forEach(x => {
          currentValue.forEach(y => {
            if (x.value === y) {
              this.choseItems.push(x);
            }
          });
        });
        this.optimizeList();
      }
    }
  }

  private optimizeList(): void {
    // check duplicate list
    this.choseItems = ObjectUtils.filterDuplicate<UserOption>(this.choseItems);

    // sort list by order fetch API
    this.choseItems = this.choseItems.sort((a, b) => a.order - b.order);

    // emit value
    this.selectItem.next(this.choseItems);
  }
}
